import { Flex, useResizeObserver } from '@cad-arc';
import { debounce } from 'lodash';
import { useState, useMemo, CSSProperties, useEffect } from 'react';
import { useResizable } from 'react-resizable-layout';

interface usePanelResizerProps {
    initialContainerWidth: number;
    initialLeftPanelWidth: number;
    minLeftPanelWidth: number;
    minRightPanelWidth: number;
    containerRef: React.MutableRefObject<HTMLDivElement | null>;
    separatorWidth: number;
}

export const VerticalResizeSeparator: React.FC<{
    width: number;
    style?: CSSProperties;
}> = ({ width, style, ...rest }) => {
    return <Flex h="100%" style={{ ...style, width }} cursor="ew-resize" {...rest} flexGrow={0} />;
};

export const usePanelResizer = ({
    initialContainerWidth,
    initialLeftPanelWidth,
    minLeftPanelWidth,
    minRightPanelWidth,
    containerRef,
    separatorWidth,
}: usePanelResizerProps) => {
    const [containerWidth, setContainerWidth] = useState<number>(initialContainerWidth);

    const debouncedOnResize = useMemo(
        () =>
            debounce(({ width }) => {
                if (width) {
                    setContainerWidth(width);
                }
            }, 20),
        [setContainerWidth]
    );

    useResizeObserver({
        ref: containerRef,
        onResize: debouncedOnResize,
    });

    useEffect(() => {
        return () => {
            debouncedOnResize.cancel();
        };
    }, [debouncedOnResize]);

    const { position: leftPanelWidth, separatorProps, setPosition } = useResizable({
        axis: 'x',
        initial: initialLeftPanelWidth,
        min: minLeftPanelWidth,
        max: Math.max(containerWidth - minRightPanelWidth, minLeftPanelWidth),
        containerRef,
    });

    const rightPanelWidth = containerWidth - leftPanelWidth - separatorWidth;
    const rightPanelUnderflow = minRightPanelWidth - rightPanelWidth;

    if (rightPanelUnderflow > 0) {
        // shrink left panel so right panel isn't smaller than minRightPanelWidth
        setPosition(leftPanelWidth - rightPanelUnderflow);
    }

    return {
        leftPanelWidth,
        separatorProps: {
            width: separatorWidth,
            ...separatorProps,
        },
    };
};
